import { z } from 'zod';

import { looseBoolean, looseString } from '@/shared/lib/looseSchema';

/** A document ASIC or the agent issued, with the money attached to it. */
export const invoiceSchema = z.object({
  id: z.string().nullish(),
  dateIssued: z.string().default(''),
  dateDue: z.string().default(''),
  datePaid: z.string().nullish(),
  /** Coerced: the API quotes money values ("329.00"), and a bare `z.number()`
   *  rejects the whole payload over one of them — which surfaces to the
   *  director as a load failure on a request that returned 200. */
  amount: z.coerce.number().default(0),
  documentURL: looseString,
  receipt: z.string().nullish(),
  /** The director told us they paid ASIC directly; not the same as settled. */
  reportedAsPaid: z.boolean().nullish(),
  dateReported: z.string().nullish(),
});

export type Invoice = z.infer<typeof invoiceSchema>;

export const solvencySchema = z.object({
  id: z.string().nullish(),
  documentURL: looseString,
  dateIssued: z.string().default(''),
});

export const asicMemberSchema = z.object({
  id: z.string().default(''),
  name1: z.string().default(''),
  name2: z.string().default(''),
  name3: z.string().default(''),
  familyName: z.string().default(''),
});

/** Per-director signing progress on one document — what the portal exists to
 *  move forward, so `hasSigned` drives most of the review UI. */
export const directorSignatureSchema = z.object({
  id: z.string().default(''),
  asicMember: asicMemberSchema,
  documentId: z.string().default(''),
  hasSigned: looseBoolean,
  signedDate: z.string().nullish(),
  viewedDate: z.string().nullish(),
});

export type DirectorSignature = z.infer<typeof directorSignatureSchema>;

export const annualPaymentSchema = z.object({
  id: z.string().nullish(),
  transactionId: z.string().default(''),
  paidDate: z.union([z.number(), z.string()]).nullish(),
  annualReviewId: z.string().nullish(),
  isAgentReported: z.boolean().nullish(),
});

export type AnnualPayment = z.infer<typeof annualPaymentSchema>;

export const annualReviewSchema = z.object({
  id: z.string().nullish(),
  asicCompanyId: z.string().nullish(),
  isCompleted: looseBoolean,
  dateIssued: z.string().default(''),
  docsSignedDate: z.string().nullish(),
  docsViewedDate: z.string().nullish(),
  agentInvoice: invoiceSchema.nullish(),
  asicInvoice: invoiceSchema.nullish(),
  solvency: solvencySchema.nullish(),
  annualPayment: annualPaymentSchema.nullish(),
  directorSignatures: z.array(directorSignatureSchema).default([]),
  paidStatus: z.string().nullish(),
});

export type AnnualReview = z.infer<typeof annualReviewSchema>;

export const reminderSchema = z.object({
  name: z.string().default(''),
  sentAt: z.string().default(''),
  email: looseBoolean,
  sms: looseBoolean,
});

export const highlightsSchema = z.object({
  dueDate: z.string().default(''),
  firstReminderSent: z.string().nullish(),
  nextReminderDate: z.string().nullish(),
  remindersEnabled: looseBoolean,
  reminders: z.array(reminderSchema).default([]),
  status: z.string().default(''),
});

/** `GET /director/{asicCompanyId}/ar` — the list plus its summary block. */
export const annualReviewListSchema = z.object({
  annualReviews: z.array(annualReviewSchema).default([]),
  highlights: highlightsSchema.nullish(),
});

export type AnnualReviewList = z.infer<typeof annualReviewListSchema>;

export const signatureSchema = z.object({
  id: z.string().default(''),
  base64Image: z.string().default(''),
});

export type Signature = z.infer<typeof signatureSchema>;

export const signatureListSchema = z.object({
  signatures: z.array(signatureSchema).default([]),
});
